// POST /api/v1/portfolios/:portfolio_id/withdraw-consent — the HEAVY
// creator action: leaves the roster for FUTURE sales AND revokes
// creator status (users.creator_status 'approved' → 'none').
// Already-sold dossiers keep featuring the creator's profile.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { attachRequestIdToScope } from "../../lib/api-client";

export type WithdrawConsentOutcome = {
  portfolio_id: string;
  withdrawn_at: string;
  creator_status_revoked: boolean;
  portfolio_stalled: boolean;
  withdrawn_count: number;
  roster_count: number;
};

export class WithdrawConsentError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

async function withdrawConsent(input: {
  portfolio_id: string;
}): Promise<WithdrawConsentOutcome> {
  const response = await fetch(
    `/api/v1/portfolios/${input.portfolio_id}/withdraw-consent`,
    { method: "POST", credentials: "include" },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let code = "UNKNOWN";
    let message = `Withdraw consent failed (HTTP ${response.status})`;
    try {
      const body = (await response.json()) as {
        error?: { code?: string; message?: string };
      };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      // non-JSON error body — keep the generic message
    }
    throw new WithdrawConsentError(code, message);
  }
  const body = (await response.json()) as { data: WithdrawConsentOutcome };
  return body.data;
}

export function useWithdrawPortfolioConsent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: withdrawConsent,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["creator-portfolios", "memberships"],
      });
      queryClient.invalidateQueries({
        queryKey: ["creator-portfolios", "recruiting"],
      });
      // Creator status was revoked — the shell nav + guards must re-read.
      queryClient.invalidateQueries({ queryKey: ["account-me"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
    onError: (error) => {
      if (error instanceof WithdrawConsentError) {
        switch (error.code) {
          case "PORTFOLIO_CONSENT_ALREADY_WITHDRAWN":
            toast.error("You've already withdrawn from this portfolio.");
            break;
          case "PORTFOLIO_MEMBERSHIP_NOT_FOUND":
            toast.error("You're not on this portfolio's roster.");
            break;
          case "RATE_LIMITED":
            toast.error("Too many withdrawals in a row — try again later.");
            break;
          default:
            toast.error(error.message);
        }
      } else {
        toast.error("Something went wrong. Try again.");
      }
    },
  });
}
